import { create } from 'zustand';
import dayjs from 'dayjs';
import { get_records, rollback_renovation_customer } from '../utils/apis/records';
import { get_customers } from '../utils/apis/customers';
import { get_renovation_nexttendays, send_email_report } from '../utils/apis/home';
import { getTempTotal } from '../utils/functions/getTotal';
import { calculateBankAmounts } from '../utils/functions/calculateBanksAmounts';
import { notifications } from '@mantine/notifications';

export const useDashboardStore = create((set) => ({
  itemsCustomers: null,
  itemsPostpago: null,
  itemsPrepago: null,
  totalPostpago: null,
  totalPrepago: null,
  totalAmount: null,
  records: null,
  renovation: null,
  banks: null,
  loadingBanks: true,
  rollbackData: null,
  openRollBack: false,
  sendingEmail: false,

  sendEmailReport: async () => {
    set({ sendingEmail: true });
    try {
      const res = await send_email_report();
      if (res?.status) {
        notifications.show({
          title: 'Reporte enviado',
          message: 'El reporte de cuentas se ha enviado correctamente por correo.',
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Error',
          message: res?.message || 'No se pudo enviar el reporte.',
          color: 'red',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error de conexión',
        message: 'Ocurrió un error al intentar enviar el reporte.',
        color: 'red',
      });
    } finally {
      set({ sendingEmail: false });
    }
  },

  fetchDashboardData: async () => {
    try {
      const startDate = dayjs().startOf('month').format('YYYY-MM-DD');
      const endDate = dayjs().endOf('month').add(1, 'day').format('YYYY-MM-DD');

      // Fetch all data in PARALLEL to reduce wait time
      const [recordsRes, customersRes, renovationRes] = await Promise.all([
        get_records({
          filter: [dayjs(startDate).format('DD/MM/YYYY'), dayjs(endDate).format('DD/MM/YYYY')],
          status: true
        }),
        get_customers(),
        get_renovation_nexttendays()
      ]);
      
      const records = recordsRes?.records || [];
      const totalAmount = recordsRes?.total_amount || 0;
      
      // Get totals per contract from backend aggregation results
      const typeContracts = recordsRes?.type_contracts || [];
      const totalPostpago = typeContracts.find(c => c.name === 'Postpago')?.amount || 0;
      const totalPrepago = typeContracts.find(c => c.name === 'Prepago')?.amount || 0;

      const customers = Array.isArray(customersRes) ? customersRes : [];
      const renovations = Array.isArray(renovationRes) ? renovationRes : [];
      const today = dayjs().format('YYYY-MM-DD');

      // Map bank data from backend
      const backendBanks = recordsRes?.type_banks || [];
      
      const predefinedBanks = [
        "Banregio",
        "Azteca",
        "BBVA",
        "Hsbc",
        "Mercado Pago",
        "Santander",
        "Efectivo",
        "Spin",
      ];

      // Merge backend results with predefined list to show 0s
      const banks = predefinedBanks.map(bankName => {
        const found = backendBanks.find(b => b.name?.toLowerCase() === bankName.toLowerCase());
        return {
          bank: bankName,
          amount: found ? found.amount : 0
        };
      });

      set({
        itemsCustomers: customers.length,
        itemsPostpago: customers.filter((item) => item.type_contract === 'Postpago').length,
        itemsPrepago: customers.filter((item) => item.type_contract === 'Prepago').length,
        totalPostpago: totalPostpago,
        totalPrepago: totalPrepago,
        totalAmount: totalAmount,
        records: [...records].reverse().slice(0, 9),
        renovation: renovations
          .filter(
            (item) => Date.parse(dayjs(item.limit_date_billing).format('YYYY-MM-DD')) >= Date.parse(today)
          )
          .sort((a, b) => new Date(a.limit_date_billing) - new Date(b.limit_date_billing))
          .slice(0, 9),
        banks: banks,
        loadingBanks: false,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      set({ loadingBanks: false });
    }
  },

  toggleRollback: (item) => set({ openRollBack: true, rollbackData: item }),

  closeRollback: () => set({ openRollBack: false, rollbackData: null }),

  rollBackRenovation: async (item) => {
    await rollback_renovation_customer({ _id: item._id });
    set((state) => {
      state.fetchDashboardData();
      return { openRollBack: false, rollbackData: null };
    });
  }
}));
